import { getAuthCookie } from "../../utils/authCookies";

const apiBase = () => String(process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");

function authHeaders(json = false): HeadersInit {
  const token = getAuthCookie("sessionToken") || "";
  return {
    Authorization: `Bearer ${token}`,
    ...(json ? { "Content-Type": "application/json" } : {}),
  };
}

export type ChatAgentOption = {
  option: number;
  label: string;
  reference_token?: string;
};

export type ChatAgentPreview = {
  vendor?: string;
  product?: string;
  sku?: string;
  quantity?: number;
  unit_price?: number;
  billing_address?: string;
  shipping_address?: string;
  shipping_method?: string;
  shipping?: number;
  special_fees?: Array<{ name?: string; amount?: number; required?: boolean }>;
  subtotal?: number;
  discount?: number;
  tax?: number;
  total?: number;
  currency?: string;
  status?: string;
};

export type ChatAgentOrder = {
  order_number?: string;
  status?: string;
  total?: number;
};

export type ChatAgentReportColumn = {
  key: string;
  label: string;
  data_type: "string" | "number" | "integer" | "datetime" | string;
  format?: "currency" | string;
};

export type ChatAgentReport = {
  report_type: string;
  title: string;
  period: string;
  columns: ChatAgentReportColumn[];
  summary: Record<string, number>;
  rows: Array<Record<string, unknown>>;
  page: number;
  page_size: number;
  has_more: boolean;
  export_available: boolean;
};

export type ChatAgentMessageResponse = {
  conversation_id?: string;
  action_id?: string;
  state?: string;
  message?: string;
  options?: ChatAgentOption[];
  preview?: ChatAgentPreview;
  order?: ChatAgentOrder;
  report?: ChatAgentReport;
  error?: string;
};

async function parseJson<T>(res: Response): Promise<T> {
  const data = (await res.json().catch(() => ({}))) as T & { error?: string; message?: string };
  if (!res.ok) {
    throw new Error(
      (data as { error?: string; message?: string })?.error ||
        (data as { error?: string; message?: string })?.message ||
        `Request failed (${res.status})`
    );
  }
  return data;
}

/** POST /chat-agent/messages */
export async function postChatAgentMessage(body: {
  conversation_id: string;
  message: string;
  input_type?: string;
}): Promise<ChatAgentMessageResponse> {
  const res = await fetch(`${apiBase()}/chat-agent/messages`, {
    method: "POST",
    headers: authHeaders(true),
    body: JSON.stringify({
      conversation_id: body.conversation_id || "",
      message: body.message,
      input_type: body.input_type || "text",
    }),
  });
  return parseJson<ChatAgentMessageResponse>(res);
}

/** POST /chat-agent/actions/{action_id}/confirm */
export async function confirmChatAgentAction(
  actionId: string
): Promise<ChatAgentMessageResponse> {
  const id = String(actionId || "").trim();
  if (!id) throw new Error("action_id is required");
  const res = await fetch(`${apiBase()}/chat-agent/actions/${id}/confirm`, {
    method: "POST",
    headers: authHeaders(),
  });
  return parseJson<ChatAgentMessageResponse>(res);
}

/** POST /chat-agent/actions/{action_id}/cancel */
export async function cancelChatAgentAction(
  actionId: string
): Promise<ChatAgentMessageResponse> {
  const id = String(actionId || "").trim();
  if (!id) throw new Error("action_id is required");
  const res = await fetch(`${apiBase()}/chat-agent/actions/${id}/cancel`, {
    method: "POST",
    headers: authHeaders(),
  });
  return parseJson<ChatAgentMessageResponse>(res);
}
